import { useLazyQuery } from "@apollo/client";
import moment from "moment";
import { useState } from "react";
import { useLocation } from "react-router-dom";
import {
  AskAiMeetingEmbedFragmentFragment,
  GetAskAiMeetingEmbedQueryQuery,
  GetAskAiMeetingEmbedQueryQueryVariables,
  SuggestedTopicItemNode,
} from "types/graphql-schema";
import { TFLocationState } from "types/topicflow";

import AddTopicToMeetingDialog from "@apps/add-topic-to-meeting-dialog/add-topic-to-meeting-dialog";
import Avatars from "@components/avatar/avatars";
import Button, { ButtonSize, buttonTheme } from "@components/button/button";
import AppLink from "@components/link/link";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import {
  assertEdgesNonNull,
  assertEdgesNonNullWithStringId,
  formatTime,
  getUrlUsingTypename,
  removeCurrentYear,
} from "@helpers/helpers";

import getAskAiMeetingEmbedQuery from "../graphql/get-ask-ai-meeting-embed-query";

const labelClassName = "w-30 tf-body-sm-light";

const AskAIEmbedSuggestedTopic = ({
  meeting,
  suggestedTopics,
}: {
  meeting?: AskAiMeetingEmbedFragmentFragment | null;
  suggestedTopics: SuggestedTopicItemNode[];
}) => {
  const location = useLocation<TFLocationState>();
  const [showTopicDialog, setShowTopicDialog] = useState<{
    topicTitle: string;
    topicNotes?: any;
  } | null>(null);

  const participantUsers = meeting?.participants
    ? assertEdgesNonNullWithStringId(meeting?.participants).map(
        (participant) => participant.user
      )
    : [];
  const topics = meeting?.topics ? assertEdgesNonNull(meeting.topics) : [];
  const existingTopicTitles = topics.map((topic) => topic.title);

  const suggestedTopicsNotAdded = suggestedTopics.filter(
    (topic) => !existingTopicTitles.includes(topic.title)
  );
  const hasBeenAdded = suggestedTopicsNotAdded.length === 0;

  const [refreshMeeting] = useLazyQuery<
    GetAskAiMeetingEmbedQueryQuery,
    GetAskAiMeetingEmbedQueryQueryVariables
  >(getAskAiMeetingEmbedQuery);

  const handleOpenDialog = (topic: SuggestedTopicItemNode) => {
    setShowTopicDialog({
      topicTitle: topic.title,
      topicNotes: topic.notes,
    });
  };

  const handleCloseDialog = () => {
    setShowTopicDialog(null);
  };

  const handleCreatedTopic = () => {
    setShowTopicDialog(null);
    if (meeting) {
      refreshMeeting({ variables: { meetingId: meeting.id } });
    }
  };

  const participantIds = participantUsers
    .filter((user) => user !== null && user !== undefined)
    .map((user) => user!.id);
  const isFormalOneonone = meeting?.meetingGroup?.isFormalOneonone ?? false;

  return meeting ? (
    <>
      {showTopicDialog && (
        <AddTopicToMeetingDialog
          topicTitle={showTopicDialog.topicTitle}
          topicNotes={showTopicDialog.topicNotes}
          initialMeetingId={meeting.id}
          variables={{
            participants: participantIds,
            isFormalOneonone: isFormalOneonone,
            startDatetimeAfter: meeting?.startDatetime,
          }}
          onClose={handleCloseDialog}
          onCreatedTopic={handleCreatedTopic}
        />
      )}
      <div className="w-full flex flex-col gap-4">
        <div className="flex gap-2 justify-between items-center">
          <div className="flex-1">
            <AppLink
              to={getUrlUsingTypename(meeting, location)}
              className="hover:underline font-medium"
            >
              {meeting.title}
            </AppLink>
          </div>
          <div className="flex gap-2 items-center">
            <Button
              text="View Meeting"
              to={getUrlUsingTypename(meeting, location)}
              size={ButtonSize.medium}
            />
          </div>
        </div>
        <div className="flex gap-1">
          <div className={labelClassName}>Details</div>
          <div className="flex items-center gap-1">
            <TopicflowIcon type="meeting" className="w-4 h-4 text-gray-400" />
            {meeting.startDatetime && meeting.endDatetime ? (
              <span>
                {removeCurrentYear(moment(meeting.startDatetime).format("ll"))},{" "}
                {formatTime(moment(meeting.startDatetime).format("h:mma"))}-
                {formatTime(moment(meeting.endDatetime).format("h:mma"))}
              </span>
            ) : (
              <span>Unscheduled</span>
            )}
          </div>
        </div>
        <div className="flex gap-1">
          <div className={labelClassName}>Participants</div>
          <div>
            <Avatars
              users={participantUsers}
              max={5}
              modalTitle="Participants"
            />
          </div>
        </div>
        <div className="flex gap-1">
          <div className={labelClassName}>Topics</div>
          <div className="flex flex-col gap-2">
            {topics.map((topic) => (
              <div className="text-gray-400" key={topic.id}>
                {topic.title}
              </div>
            ))}

            {!hasBeenAdded &&
              suggestedTopicsNotAdded.map((suggestedTopic) => {
                const hasNotes =
                  suggestedTopic.notes &&
                  (Array.isArray(suggestedTopic.notes)
                    ? suggestedTopic.notes.length > 0
                    : false);
                return (
                  <div
                    key={suggestedTopic.title}
                    className="flex items-start gap-2"
                  >
                    <div className="flex-1">
                      <button
                        type="button"
                        className="font-medium text-left hover:underline"
                        onClick={() => handleOpenDialog(suggestedTopic)}
                      >
                        {suggestedTopic.title}
                      </button>
                      {hasNotes && (
                        <div className="text-sm text-gray-500 mt-1 ml-4">
                          {Array.isArray(suggestedTopic.notes) ? (
                            <ul className="list-disc">
                              {suggestedTopic.notes.map((note, idx) => (
                                <li key={idx}>{note}</li>
                              ))}
                            </ul>
                          ) : (
                            <div>{String(suggestedTopic.notes)}</div>
                          )}
                        </div>
                      )}
                    </div>
                    <Button
                      text="Add & Edit"
                      size={ButtonSize.small}
                      theme={buttonTheme.lightBlue}
                      onClick={() => handleOpenDialog(suggestedTopic)}
                    />
                  </div>
                );
              })}
          </div>
        </div>
      </div>
    </>
  ) : (
    <div>
      The meeting could not be found. Please provide more details about the
      meeting your are trying to suggest topics for.
    </div>
  );
};

export default AskAIEmbedSuggestedTopic;
